import React from 'react';
import { AdminSidebarComponentProps } from '../JsonComponent.types';
import { LayoutAddComponentListItem } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutAddComponentListItem';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useGoToCreateView } from '../hooks/useJsonComponentNavigation';
import { ComponentType } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { SimpleGrabHandle } from '@website-editor/components/SimpleGrabHandle';
import { WeatherReportConfig } from './WeatherReport.config';
import { Box, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core/i18n';
import { createWeatherReportTemplate, deriveLegTitle } from './WeatherReport.helpers';
import type { WeatherReportContentV103 } from './WeatherReport.types';
import { WEATHER_REPORT_SCHEMA_URL } from './WeatherReport.constants';
import { InfoTooltip } from '@shared/components/InfoTooltip';
import { SunOutline } from '@withjoy/joykit/icons';
import { useCustomComponentTelemetry } from '../createCustomComponentTelemetry';

export const WeatherReportGrabber: React.FC<AdminSidebarComponentProps> = props => {
  const { tq } = useTranslation('websiteEditor');
  const content = props.componentData.containerContent.content as WeatherReportContentV103 | null | undefined;
  const firstLeg = content?.itinerary?.[0];
  const title = firstLeg ? deriveLegTitle(firstLeg, tq`Weather Report`) : tq`Weather Report`;
  return (
    <SimpleGrabHandle
      title={title}
      icon={<SunOutline size={32} />}
      {...props}
    ></SimpleGrabHandle>
  );
};

export const WeatherReportAdd: React.FC<AdminSidebarComponentProps> = props => {
  const { tq } = useTranslation('websiteEditor');
  const { goToCreateView } = useGoToCreateView();
  const eventInfo = useEventInfo();
  const eventLocation = eventInfo?.eventInfo?.locationInfo?.city;
  const country = eventInfo?.eventInfo?.locationInfo?.country;
  const eventId = eventInfo?.eventInfo?.eventId;
  const telemetry = useCustomComponentTelemetry('WeatherReport', 'weatherReportComponent');

  const handleClick = useEventCallback(() => {
    telemetry.beginAdding('Custom page', props.pageSlug);
    const initial = createWeatherReportTemplate();
    const [firstItineraryLeg] = initial.itinerary;
    if (firstItineraryLeg) {
      firstItineraryLeg.label = eventLocation || firstItineraryLeg.label;
      firstItineraryLeg.location = {
        ...(firstItineraryLeg.location || {}),
        city: eventLocation || firstItineraryLeg.location?.city,
        country: country || firstItineraryLeg.location?.country,
      };
    }

    goToCreateView({
      id: props.containerId,
      containerContent: {
        id: props.containerId,
        type: WeatherReportConfig.componentType,
        content: initial,
        eventId: eventId || '',
        schemaUrl: WEATHER_REPORT_SCHEMA_URL,
      },
      type: ComponentType.jsonDataBlob,
      sortOrder: 0,
      jsonDataContainerId: props.containerId,
      __typename: 'JsonDataBlobComponent',
    });
  });

  return (
    <InfoTooltip
      title={tq`What's Weather Report`}
      description={tq`Share the local weather forecast for your event dates, helping guests pack and plan appropriately.`}
    >
      <Box>
        <LayoutAddComponentListItem onClick={handleClick}>
          <SunOutline size={48} />
          <TextV2
            typographyVariant="hed2"
            textAlign="left"
          >
            {tq`Weather Report`}
          </TextV2>
        </LayoutAddComponentListItem>
      </Box>
    </InfoTooltip>
  );
};
